import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import API from '../api/axios';
import '../styles/Swipe.css';
import NotificationBell from '../components/NotificationBell';
import SwipeLimitModal from '../components/SwipeLimitModal';

const SWIPE_THRESHOLD = 60;

export default function SwipePage() {
  const [candidates, setCandidates] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [exiting, setExiting] = useState(null);
  const [matchData, setMatchData] = useState(null);
  const [history, setHistory] = useState([]);
  const navigate = useNavigate();
  const [limitInfo, setLimitInfo] = useState(null); // holds { message, upgrade } when blocked

   const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchCandidates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await API.get('/api/discovery', { params: { page: 1 } });
      const newCandidates = response.data.candidates || [];
      setCandidates(newCandidates);
      setCurrentIndex(0);
      setPage(1);
      setHasMore(response.data.has_more === true);
    } catch (err) {
      console.error('Failed to fetch candidates:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // When the user gets close to the end of the loaded batch, quietly fetch
  // the next page and append it, so they never hit "no more profiles" while
  // there are still eligible candidates left.
  const fetchMoreCandidates = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const response = await API.get('/api/discovery', { params: { page: nextPage } });
      const more = response.data.candidates || [];
      setCandidates((prev) => [...prev, ...more]);
      setPage(nextPage);
      setHasMore(response.data.has_more === true);
    } catch (err) {
      console.error('Failed to fetch more candidates:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [page, hasMore, loadingMore]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  // Prefetch the next page once the user is a few cards away from the end.
  useEffect(() => {
    if (hasMore && !loadingMore && candidates.length - currentIndex <= 5) {
      fetchMoreCandidates();
    }
  }, [currentIndex, candidates.length, hasMore, loadingMore, fetchMoreCandidates]);
  useEffect(() => {
    setPhotoIndex(0);
  }, [currentIndex]);

  const currentCandidate = candidates[currentIndex];
const handleSwipe = async (direction) => {
  if (!currentCandidate || exiting) return;

  let matched = false;
  try {
    const response = await API.post('/api/swipes', {
      target_id: currentCandidate.user_id,
      direction: direction,
    });
    matched = response.data?.matched === true;
  } catch (err) {
    if (err.response?.status === 403 && err.response?.data?.error === 'limit_reached') {
      setLimitInfo(err.response.data); // { error, message, upgrade }
      return; // card stays put, no exit animation
    }
    // Any other error: keep prior behavior of advancing anyway
  }

  // Remember this card so the rewind button has something to restore.
  setHistory((h) => [...h, currentCandidate]);

  setExiting(direction === 'like' ? 'right' : 'left');

  setTimeout(() => {
    if (matched) {
      setMatchData({
        name: currentCandidate.full_name,
        photo: currentCandidate.photos?.[0]?.url,
        user_id: currentCandidate.user_id,
      });
    }
    setCurrentIndex((i) => i + 1);
    setExiting(null);
  }, 300);
};
  const handleDragEnd = (e, info) => {
  const swipePower = Math.abs(info.offset.x) * info.velocity.x;

  if (info.offset.x > SWIPE_THRESHOLD || swipePower > 5000) {
    handleSwipe('like');
  } else if (
    info.offset.x < -SWIPE_THRESHOLD ||
    swipePower < -5000
  ) {
    handleSwipe('pass');
  }
};

  const handlePhotoNav = (direction) => {
    if (!currentCandidate?.photos?.length) return;
    const max = currentCandidate.photos.length;
    if (direction === 'next' && photoIndex < max - 1) {
      setPhotoIndex(photoIndex + 1);
    } else if (direction === 'prev' && photoIndex > 0) {
      setPhotoIndex(photoIndex - 1);
    }
  };

  const dismissMatch = () => setMatchData(null);

  const goToChat = () => {
    setMatchData(null);
    navigate('/matches');
  };

  if (loading) {
    return (
      <div className="swipe-page">
        <header className="swipe-header">
          <NotificationBell />
        </header>
        <div className="swipe-loading">Finding profiles...</div>
      </div>
    );
  }

  if (!currentCandidate) {
    return (
      <div className="swipe-page">
        <header className="swipe-header">
          <NotificationBell />
        </header>
        <div className="swipe-empty">
          <h2>No new profiles right now</h2>
          <p>Check back soon.</p>
          <button onClick={fetchCandidates} className="refresh-button">
            Refresh
          </button>
        </div>
      </div>
    );
  }

  const photos = currentCandidate.photos || [];
  const currentPhoto = photos[photoIndex];

  return (
    <div className="swipe-page">
      <header className="swipe-header">
        <NotificationBell />
      </header>

      <div className="card-stack">
        <motion.div
          key={currentCandidate.user_id}
          className="swipe-card"
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.25}
          dragMomentum={false}
          onDragEnd={handleDragEnd}
          animate={
            exiting
              ? {
                  x: exiting === 'right' ? 1000 : -1000,
                  rotate: exiting === 'right' ? 25 : -25,
                  opacity: 0,
                }
              : { x: 0, rotate: 0, opacity: 1 }
          }
          transition={{ duration: 0.3 }}
          whileDrag={{ scale: 1.02 }}
        >
          <div className="card-photo">
            {currentPhoto ? (
              <img src={currentPhoto.url} alt="" />
            ) : (
              <div className="photo-placeholder">No photo</div>
            )}

            {photos.length > 1 && (
              <div className="photo-dots">
                {photos.map((_, i) => (
                  <span
                    key={i}
                    className={`photo-dot ${i === photoIndex ? 'active' : ''}`}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => setPhotoIndex(i)}
                    style={{ cursor: 'pointer' }}
                  />
                ))}
              </div>
            )}

            {photos.length > 1 && (
              <>
                <div className="photo-nav-zone left" onClick={() => handlePhotoNav('prev')} />
                <div className="photo-nav-zone right" onClick={() => handlePhotoNav('next')} />
              </>
            )} 

            <div className="card-info" onPointerDown={(e) => e.stopPropagation()}>
              <div className="card-name-row">
                <h2>{currentCandidate.full_name}</h2>
                {currentCandidate.age != null && (
                  <span className="card-age">{currentCandidate.age}</span>
                )}
              </div>
              {currentCandidate.denomination && (
                <span className="card-badge">{currentCandidate.denomination}</span>
              )}
              {currentCandidate.distance_km != null && (
                <p className="card-distance">
                  {Math.round(currentCandidate.distance_km)} km away
                </p>
              )}
              {currentCandidate.bio && (
                <p className="card-bio">{currentCandidate.bio}</p>
              )}
              <button
                className="view-profile-btn"
                onClick={() => navigate(`/users/${currentCandidate.user_id}`)}
              >
                View Profile →
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="action-buttons">
        <button
          className="action-btn rewind-btn"
         onClick={async () => {
  if (history.length === 0) return;

  try {
    await API.post('/api/swipes/rewind');

    const previousCard = history[history.length - 1];

    if (!previousCard) return;

    setCandidates((prev) => {
      const updated = [...prev];
      updated.splice(currentIndex, 0, previousCard);
      return updated;
    });

    setHistory((h) => h.slice(0, -1));
   } catch (err) {
    if (err.response?.data?.error === 'platinum_required') {
      setLimitInfo(err.response.data);
    }
  }
}}
          aria-label="Rewind"
          style={{
            background: 'white',
            color: '#f39c12',
            opacity: history.length > 0 ? 1 : 0.4,
            pointerEvents: history.length > 0 ? 'auto' : 'none',
          }}
        >
          ↩
        </button>

        <button
          className="action-btn pass-btn"
          onClick={() => handleSwipe('pass')}
          disabled={!!exiting}
          aria-label="Pass"
          style={{ background: 'white', color: '#27ae60' }}
        >
          ✕
        </button>

        <button
          className="action-btn like-btn"
          onClick={() => handleSwipe('like')}
          disabled={!!exiting}
          aria-label="Like"
          style={{ background: 'white', color: '#27ae60' }}
        >
          ♥
        </button>
      </div>

      <AnimatePresence>
        {matchData && (
          <motion.div
            className="match-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={dismissMatch}
          >
            <motion.div
              className="match-modal"
              initial={{ scale: 0.5 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.5 }}
              transition={{ type: 'spring', duration: 0.4 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h1>It's a Match!</h1>
              <p>You and {matchData.name} liked each other</p>
              {matchData.photo && (
                <img src={matchData.photo} alt={matchData.name} />
              )}
              <div className="match-actions">
                <button onClick={dismissMatch}>Keep swiping</button>
                <button onClick={goToChat} className="primary">
                  Send a message
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {limitInfo && (
  <SwipeLimitModal
    message={limitInfo.message}
    onClose={() => setLimitInfo(null)}
    onUpgrade={() => navigate('/premium')}
  />
)}
    </div>
  );
}